import { DestroyRef, DOCUMENT, inject, NgZone, Service } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from './auth.service';
import { CHANNEL_NAME, INACTIVITY_TIMEOUT_MS, STORAGE_KEY } from '../consts/seassionTimeoutConsts';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  fromEvent,
  merge,
  startWith,
  Subject,
  switchMap,
  takeUntil,
  throttleTime,
  timer,
} from 'rxjs';
import { SessionExpiredModalComponent } from '../components/session-expired-modal/session-expired-modal.component';

@Service()
export class SessionTimeoutService {
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);
  private readonly dialog = inject(MatDialog);
  private readonly authService = inject(AuthService);

  private broadcastChannel?: BroadcastChannel;
  private readonly stop$ = new Subject<void>();

  public startMonitoring(): void {
    this.stopMonitoring();

    if ('BroadcastChannel' in window) {
      this.broadcastChannel = new BroadcastChannel(CHANNEL_NAME);
      this.broadcastChannel.onmessage = (event) => {
        if (event.data === 'USER_ACTIVE') {
          this.updateTimestamp(false);
        }
      };
    }

    this.updateTimestamp(true);

    this.ngZone.runOutsideAngular(() => {
      const activity$ = merge(
        fromEvent(this.document, 'mousemove'),
        fromEvent(this.document, 'keydown'),
        fromEvent(this.document, 'click'),
        fromEvent(this.document, 'scroll'),
        fromEvent(this.document, 'touchstart'),
      ).pipe(throttleTime(1000), takeUntil(this.stop$), takeUntilDestroyed(this.destroyRef));

      activity$.subscribe(() => {
        this.updateTimestamp(true);
      });

      activity$
        .pipe(
          startWith(null),
          switchMap(() => timer(INACTIVITY_TIMEOUT_MS)),
          takeUntil(this.stop$),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => {
          this.checkAndHandleTimeout();
        });

      fromEvent(this.document, 'visibilitychange')
        .pipe(takeUntil(this.stop$), takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          if (this.document.visibilityState === 'visible') {
            this.checkAndHandleTimeout();
          }
        });
    });
  }

  public stopMonitoring(): void {
    this.stop$.next();

    if (this.broadcastChannel) {
      this.broadcastChannel.close();
      this.broadcastChannel = undefined;
    }
  }

  private updateTimestamp(notifyOtherTabs: boolean): void {
    const now = Date.now();
    localStorage.setItem(STORAGE_KEY, now.toString());

    if (notifyOtherTabs && this.broadcastChannel) {
      this.broadcastChannel.postMessage('USER_ACTIVE');
    }
  }

  private checkAndHandleTimeout(): void {
    const lastActivity = Number(localStorage.getItem(STORAGE_KEY) || '0');
    const now = Date.now();

    if (now - lastActivity >= INACTIVITY_TIMEOUT_MS) {
      this.ngZone.run(() => {
        this.executeLogout();
      });
    }
  }

  private executeLogout(): void {
    this.stopMonitoring();
    localStorage.removeItem(STORAGE_KEY);

    this.authService.logout();

    this.dialog.open(SessionExpiredModalComponent, {
      width: '40rem',
      disableClose: true,
    });
  }
}
